import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { User, Star, Search } from "lucide-react";

export function PlayerAutocomplete({ value, onChange, onSelect, favorites, excludeNames, placeholder, testId }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const latestProps = useRef({ favorites, excludeNames });
  latestProps.current = { favorites, excludeNames };

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchUsers = useCallback(async (term) => {
    if (term.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/users/search?q=${encodeURIComponent(term)}`, { credentials: "include" });
      if (res.ok) {
        const users = await res.json();
        const { favorites: currentFavs, excludeNames: currentExclude } = latestProps.current;
        const excludeLower = (currentExclude || []).map(n => n.toLowerCase().trim());
        const favResults = (currentFavs || [])
          .filter(f => f.name.toLowerCase().includes(term.toLowerCase()) && !excludeLower.includes(f.name.toLowerCase().trim()))
          .map(f => ({ type: "favorite", id: f.player_id, name: f.name, handicap: f.handicap }));
        const profileResults = users
          .filter(u => !excludeLower.includes(u.name?.toLowerCase().trim()))
          .map(u => ({ type: "profile", id: u.user_id, name: u.name, email: u.email, handicap: u.handicap_index, club: u.club }));
        const seen = new Set();
        const merged = [];
        for (const p of profileResults) {
          const key = p.name.toLowerCase().trim();
          if (!seen.has(key)) { seen.add(key); merged.push(p); }
        }
        for (const f of favResults) {
          const key = f.name.toLowerCase().trim();
          if (!seen.has(key)) { seen.add(key); merged.push(f); }
        }
        setSuggestions(merged.slice(0, 8));
      }
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim().length >= 2) {
        searchUsers(val.trim());
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 250);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.name);
    setShowDropdown(false);
    setSuggestions([]);
    onSelect(suggestion);
  };

  const handleFocus = () => {
    if (query.trim().length >= 2 && suggestions.length > 0) setShowDropdown(true);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
        <Input
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={placeholder || "Buscar jugador..."}
          className="input-default pl-8"
          data-testid={testId || "player-autocomplete"}
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="autocomplete-dropdown">
          {suggestions.map((s) => (
            <button
              key={`${s.type}-${s.id}`}
              type="button"
              className="w-full px-3 py-2.5 text-left hover:bg-[#1B4D3E]/5 transition-colors flex items-center gap-2.5 border-b border-gray-50 last:border-0"
              onClick={() => handleSelect(s)}
              data-testid={`suggestion-${s.type}-${s.id}`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                s.type === "profile" ? "bg-[#1B4D3E]/10 text-[#1B4D3E]" : "bg-amber-50 text-amber-600"
              }`}>
                {s.type === "profile" ? <User className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{s.name}</p>
                <p className="text-[11px] text-[#64748B] truncate">
                  {s.type === "profile" ? (
                    <>{s.club || s.email}{s.handicap != null ? ` · HCP Index: ${s.handicap}` : ""}</>
                  ) : (
                    <>Favorito · HCP: {s.handicap}</>
                  )}
                </p>
              </div>
              {s.handicap != null && (
                <span className="text-xs font-bold text-[#1B4D3E] bg-[#1B4D3E]/10 px-2 py-0.5 rounded-full shrink-0">
                  {s.handicap}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
